// Pre-seeded data for VOFF Compliance Watchdog

export type CertStatus = "EXPIRED" | "CRITICAL" | "WARNING" | "VALID";
export type EscalationType = "AUTO-90" | "AUTO-60" | "AUTO-30" | "AUTO-EXPIRED" | "MANUAL";

export interface Certificate {
  id: string;
  vessel?: string;
  crewMember?: string;
  crewRole?: string;
  name: string;
  issuingAuthority: string;
  issueDate: string;
  expiryDate: string;
  category: string;
  owner: string;
  notes?: string;
  lastEscalationSent?: string;
  documentRef?: string;
}

export interface CrewMember {
  id: string;
  name: string;
  role: string;
  vessel: string;
  nationality: string;
  photoUrl?: string;
  certificates: Certificate[];
}

export interface EscalationEntry {
  id: string;
  timestamp: string;
  recipient: string;
  certificate: string;
  daysAtSend: number;
  type: EscalationType;
  deliveryStatus: "sent" | "opened" | "failed";
  emailSubject: string;
  vesselOrName: string;
}

export function getCertStatus(expiryDate: string): { status: CertStatus; daysRemaining: number } {
  const now = new Date();
  const expiry = new Date(expiryDate);
  const diff = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  if (diff <= 0) return { status: "EXPIRED", daysRemaining: diff };
  if (diff <= 30) return { status: "CRITICAL", daysRemaining: diff };
  if (diff <= 90) return { status: "WARNING", daysRemaining: diff };
  return { status: "VALID", daysRemaining: diff };
}

const VESSELS = ["VOFF Crewliner 01", "VOFF Crewliner 02", "VOFF Combi 01"];

function dateOffset(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().split("T")[0];
}

function issueDateBefore(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().split("T")[0];
}

const FLEET_CERT_TEMPLATES = [
  { name: "ABS Classification Certificate", authority: "ABS", category: "Class", owner: "Technical Manager" },
  { name: "RMI Certificate of Registry", authority: "IRI/RMI", category: "Flag State", owner: "COO" },
  { name: "RMI Safety Management Certificate (ISM)", authority: "IRI/RMI", category: "Flag State", owner: "COO" },
  { name: "RMI Document of Compliance (DOC)", authority: "IRI/RMI", category: "Flag State", owner: "COO" },
  { name: "ISPS Ship Security Certificate", authority: "IRI/RMI", category: "Flag State", owner: "SSO" },
  { name: "Cargo Ship Safety Equipment Certificate", authority: "ABS", category: "Safety Equipment", owner: "Technical Manager" },
  { name: "Load Line Certificate", authority: "ABS", category: "Class", owner: "Technical Manager" },
  { name: "IOPP Certificate", authority: "ABS", category: "Environment", owner: "Technical Manager" },
  { name: "P&I Entry Certificate", authority: "Gard P&I", category: "Insurance", owner: "CEO" },
  { name: "Hull & Machinery Insurance Certificate", authority: "Lloyd's", category: "Insurance", owner: "CEO" },
  { name: "DP Equipment Class Certificate", authority: "ABS", category: "Navigation", owner: "Technical Manager" },
  { name: "GMDSS Radio Station Licence", authority: "RMI", category: "Navigation", owner: "Master" },
  { name: "Oil Record Book (current)", authority: "Vessel", category: "Environment", owner: "Chief Engineer" },
];

// Vary expiry dates to show all statuses
const EXPIRY_OFFSETS = [
  -15, // expired
  5, // critical
  22, // critical
  45, // warning
  60, // warning
  75, // warning
  120, // valid
  180, // valid
  200, // valid
  365, // valid
  400, // valid
  500, // valid
  -5, // expired
];

export function generateFleetCertificates(): Certificate[] {
  const certs: Certificate[] = [];
  let id = 1;
  VESSELS.forEach((vessel) => {
    FLEET_CERT_TEMPLATES.forEach((tmpl, idx) => {
      const offset = EXPIRY_OFFSETS[idx % EXPIRY_OFFSETS.length];
      certs.push({
        id: `fc-${id++}`,
        vessel,
        name: tmpl.name,
        issuingAuthority: tmpl.authority,
        issueDate: issueDateBefore(365),
        expiryDate: dateOffset(offset + (VESSELS.indexOf(vessel) * 10)),
        category: tmpl.category,
        owner: tmpl.owner,
      });
    });
  });
  return certs;
}

const CREW_CERT_TEMPLATES = [
  { name: "STCW Basic Safety Training (BST)", authority: "Flag State Administration" },
  { name: "Medical Fitness Certificate (ENG1)", authority: "Approved Doctor" },
  { name: "STCW Proficiency in Survival Craft", authority: "Flag State" },
  { name: "Advanced Fire Fighting (STCW)", authority: "Flag State" },
  { name: "Medical First Aid (STCW)", authority: "Flag State" },
  { name: "Proficiency in Fast Rescue Boats", authority: "Flag State" },
  { name: "GMDSS GOC", authority: "Flag State" },
  { name: "DP Operator Certificate (DPOP)", authority: "Nautical Institute" },
  { name: "DP Operator Revalidation", authority: "Nautical Institute" },
  { name: "RMI Flag State Endorsement", authority: "IRI/RMI" },
  { name: "Yellow Fever Vaccination", authority: "Approved Doctor" },
  { name: "HUET (Helicopter Underwater Escape)", authority: "Approved Centre" },
  { name: "ExxonMobil SEMS II Orientation", authority: "ExxonMobil" },
  { name: "H2S Awareness", authority: "Approved Centre" },
];

const MASTER_EXTRA_CERTS = [
  { name: "Master <500 GT Certificate", authority: "Flag State" },
  { name: "Ship Security Officer (SSO) Certificate", authority: "Flag State" },
  { name: "OVMSA2 Familiarisation Record", authority: "VOFF Internal" },
];

const CREW_MEMBERS_DATA = [
  { name: "Capt. James Morrison", role: "Master", vessel: "VOFF Crewliner 01", nationality: "British" },
  { name: "Capt. Paulo Mendes", role: "Master", vessel: "VOFF Crewliner 02", nationality: "Portuguese" },
  { name: "C/O David Chen", role: "Chief Officer", vessel: "VOFF Crewliner 01", nationality: "Singaporean" },
  { name: "Eng. Aleksander Novak", role: "Chief Engineer", vessel: "VOFF Combi 01", nationality: "Croatian" },
  { name: "DPO Sarah Williams", role: "DP Operator", vessel: "VOFF Combi 01", nationality: "Australian" },
  { name: "AB Michael Okafor", role: "Bosun / Deck Rating", vessel: "VOFF Crewliner 02", nationality: "Nigerian" },
];

const CREW_EXPIRY_OFFSETS = [-10, 8, 25, 42, 55, 88, 110, 150, 200, 300, 365, -3, 18, 70];

export function generateCrewMembers(): CrewMember[] {
  return CREW_MEMBERS_DATA.map((cm, cmIdx) => {
    const templates = [...CREW_CERT_TEMPLATES];
    if (cm.role === "Master") templates.push(...MASTER_EXTRA_CERTS);
    
    const certificates: Certificate[] = templates.map((tmpl, idx) => ({
      id: `cc-${cmIdx}-${idx}`,
      crewMember: cm.name,
      crewRole: cm.role,
      name: tmpl.name,
      issuingAuthority: tmpl.authority,
      issueDate: issueDateBefore(300 + idx * 20),
      expiryDate: dateOffset(CREW_EXPIRY_OFFSETS[(idx + cmIdx * 3) % CREW_EXPIRY_OFFSETS.length]),
      category: "Crew",
      owner: cm.name,
    }));

    return {
      id: `crew-${cmIdx}`,
      name: cm.name,
      role: cm.role,
      vessel: cm.vessel,
      nationality: cm.nationality,
      certificates,
    };
  });
}

export const COMPANY_DOCUMENTS: Certificate[] = [
  { id: "cd-1", name: "ISM Safety Management Certificate", documentRef: "DOC-001", issuingAuthority: "RMI", issueDate: issueDateBefore(300), expiryDate: dateOffset(180), category: "Company", owner: "COO" },
  { id: "cd-2", name: "Document of Compliance (DOC)", documentRef: "DOC-002", issuingAuthority: "RMI", issueDate: issueDateBefore(300), expiryDate: dateOffset(200), category: "Company", owner: "COO" },
  { id: "cd-3", name: "OVMSA2 Self-Assessment", documentRef: "OVMSA-001", issuingAuthority: "VOFF Internal", issueDate: issueDateBefore(200), expiryDate: dateOffset(45), category: "Company", owner: "COO" },
  { id: "cd-4", name: "ISO 9001 Certificate", documentRef: "ISO-001", issuingAuthority: "BSI", issueDate: issueDateBefore(100), expiryDate: dateOffset(265), category: "Company", owner: "COO" },
  { id: "cd-5", name: "VOFF SMS Manual (master)", documentRef: "SMS-001", issuingAuthority: "VOFF Internal", issueDate: issueDateBefore(250), expiryDate: dateOffset(15), category: "Company", owner: "Ops Director" },
  { id: "cd-6", name: "VOFF Cyber Security Policy", documentRef: "Corp008", issuingAuthority: "VOFF Internal", issueDate: issueDateBefore(200), expiryDate: dateOffset(-8), category: "Company", owner: "COO" },
  { id: "cd-7", name: "P&I Club Cover Letter", documentRef: "INS-001", issuingAuthority: "Gard P&I", issueDate: issueDateBefore(300), expiryDate: dateOffset(60), category: "Company", owner: "CEO" },
  { id: "cd-8", name: "Hull & Machinery Policy Summary", documentRef: "INS-002", issuingAuthority: "Lloyd's", issueDate: issueDateBefore(300), expiryDate: dateOffset(60), category: "Company", owner: "CEO" },
  { id: "cd-9", name: "RMI Officer Registration — Kyle Pemberton", documentRef: "RMI-KP", issuingAuthority: "IRI/RMI", issueDate: issueDateBefore(400), expiryDate: dateOffset(300), category: "Company", owner: "COO" },
  { id: "cd-10", name: "RMI Officer Registration — Eduard Ercegovic", documentRef: "RMI-EE", issuingAuthority: "IRI/RMI", issueDate: issueDateBefore(400), expiryDate: dateOffset(300), category: "Company", owner: "Ops Director" },
  { id: "cd-11", name: "ExxonMobil OVID Approval", documentRef: "COM-001", issuingAuthority: "ExxonMobil", issueDate: issueDateBefore(100), expiryDate: dateOffset(265), category: "Company", owner: "CCO" },
  { id: "cd-12", name: "Azule Energy Pre-Qualification", documentRef: "COM-002", issuingAuthority: "Azule Energy", issueDate: issueDateBefore(100), expiryDate: dateOffset(265), category: "Company", owner: "CCO" },
  { id: "cd-13", name: "Kongsberg Maritime NDA", documentRef: "NDA-KM-001", issuingAuthority: "Kongsberg", issueDate: issueDateBefore(500), expiryDate: dateOffset(595), category: "Company", owner: "COO" },
  { id: "cd-14", name: "Magnum Maritime — VOFF Services Agreement", documentRef: "SVC-001", issuingAuthority: "VOFF / Magnum", issueDate: issueDateBefore(200), expiryDate: dateOffset(165), category: "Company", owner: "COO" },
  { id: "cd-15", name: "VOFF LLCA (executed)", documentRef: "CORP-001", issuingAuthority: "RMI", issueDate: issueDateBefore(500), expiryDate: dateOffset(1000), category: "Company", owner: "CEO" },
];

export function generateEscalationLog(fleetCerts: Certificate[], crewMembers: CrewMember[]): EscalationEntry[] {
  const log: EscalationEntry[] = [];
  let id = 1;

  // Generate some sample escalation entries from certs in warning/critical status
  const allCerts = [
    ...fleetCerts.filter(c => { const s = getCertStatus(c.expiryDate); return s.status !== "VALID"; }),
    ...crewMembers.flatMap(cm => cm.certificates.filter(c => { const s = getCertStatus(c.expiryDate); return s.status !== "VALID"; })),
  ];

  allCerts.slice(0, 12).forEach((cert, idx) => {
    const { daysRemaining } = getCertStatus(cert.expiryDate);
    let type: EscalationType = "AUTO-90";
    if (daysRemaining <= 0) type = "AUTO-EXPIRED";
    else if (daysRemaining <= 30) type = "AUTO-30";
    else if (daysRemaining <= 60) type = "AUTO-60";
    else type = "AUTO-90";

    const ts = new Date();
    ts.setDate(ts.getDate() - idx);
    
    log.push({
      id: `esc-${id++}`,
      timestamp: ts.toISOString(),
      recipient: cert.owner || cert.crewMember || "Ops Director",
      certificate: cert.name,
      daysAtSend: daysRemaining,
      type,
      deliveryStatus: idx % 5 === 0 ? "failed" : idx % 3 === 0 ? "opened" : "sent",
      emailSubject: `[VOFF] ${type === "AUTO-EXPIRED" ? "⚠️ CERTIFICATE EXPIRED" : type === "AUTO-30" ? "URGENT: Certificate Expiry in 30 Days" : "Certificate Renewal Required"} — ${cert.name} — ${cert.vessel || cert.crewMember}`,
      vesselOrName: cert.vessel || cert.crewMember || "",
    });
  });

  return log;
}
